/**
 * Paginação do documento impresso do Design System — funções PURAS.
 *
 * Uma folha A4 é uma página. Quem mede é `medir.ts` (altura de cada bloco e os
 * pontos onde ele pode ser cortado sem partir uma linha ao meio); aqui só se
 * decide em que folha cada bloco cai, para o sumário e o rodapé "n/N" saírem
 * exatos do próprio DOM, sem marcador nem pós-processamento do PDF.
 *
 * Regras:
 * 1. Toda seção abre numa folha nova, com o cabeçalho da seção (por isso a
 *    primeira folha de cada seção tem capacidade própria, medida).
 * 2. Empacotamento guloso: o bloco entra na folha corrente se couber; senão
 *    vai inteiro para a folha seguinte. Bloco é unidade atômica.
 * 3. Bloco maior que uma folha inteira ganha folha própria e é fatiado nos
 *    cortes seguros que a medição achou — primeiro entre itens do catálogo,
 *    depois por dentro de um item. Sem corte seguro dentro da folha,
 *    o corte é forçado na altura útil, e a paginação devolve um aviso.
 * 4. `novaPagina` força o bloco a abrir folha (páginas de tema; plano B, em
 *    que cada folha é declarada à mão).
 */

/** Tolerância, em px, para arredondamento de medida. */
const FOLGA = 0.5;

export interface BlocoMedido {
  /** Altura do bloco, em px CSS, já com o espaçamento que o separa do próximo. */
  altura: number;
  /** Pontos de corte seguros, em px a partir do topo do bloco, em ordem crescente. */
  cortes: number[];
  /**
   * Cortes seguros por dentro de um item do catálogo: usados só quando não há
   * corte entre itens na folha (o item sozinho é maior que ela).
   */
  cortesInternos?: number[];
  /** Abre folha nova mesmo que caiba na corrente. */
  novaPagina?: boolean;
}

export interface SecaoMedida {
  /** Altura útil da primeira folha da seção (a que traz o cabeçalho da seção). */
  capacidadePrimeira: number;
  blocos: BlocoMedido[];
}

/** Pedaço de um bloco numa folha. Bloco inteiro: `inicio` 0 e `fim` igual à altura. */
export interface Peca {
  bloco: number;
  inicio: number;
  fim: number;
}

export interface Pagina {
  secao: number;
  /** Folha de continuação da seção (sem o cabeçalho completo). */
  continuacao: boolean;
  pecas: Peca[];
}

export interface Aviso {
  secao: number;
  bloco: number;
  motivo: 'corte-forcado';
}

export interface Paginacao {
  paginas: Pagina[];
  /** Índice, em `paginas`, da primeira folha de cada seção. */
  inicioDaSecao: number[];
  avisos: Aviso[];
}

/**
 * Empacota os blocos de cada seção em folhas.
 *
 * @param capacidadeContinuacao altura útil de uma folha de continuação.
 */
export function paginar(secoes: SecaoMedida[], capacidadeContinuacao: number): Paginacao {
  const paginas: Pagina[] = [];
  const inicioDaSecao: number[] = [];
  const avisos: Aviso[] = [];

  // Folha sem altura útil não anda: o laço de fatiamento nunca terminaria.
  const capacidades = [capacidadeContinuacao, ...secoes.map((s) => s.capacidadePrimeira)];
  if (capacidades.some((c) => !(c > 1))) {
    throw new Error('Folha sem altura útil: a medição não encontrou o corpo da folha.');
  }

  secoes.forEach((secao, indiceDaSecao) => {
    let atual: Pagina = { secao: indiceDaSecao, continuacao: false, pecas: [] };
    let restante = secao.capacidadePrimeira;
    paginas.push(atual);
    inicioDaSecao.push(paginas.length - 1);

    const novaFolha = () => {
      atual = { secao: indiceDaSecao, continuacao: true, pecas: [] };
      restante = capacidadeContinuacao;
      paginas.push(atual);
    };

    secao.blocos.forEach((bloco, indiceDoBloco) => {
      if (bloco.novaPagina && atual.pecas.length > 0) novaFolha();

      if (bloco.altura <= restante + FOLGA) {
        atual.pecas.push({ bloco: indiceDoBloco, inicio: 0, fim: bloco.altura });
        restante -= bloco.altura;
        return;
      }

      if (atual.pecas.length > 0) {
        novaFolha();
      } else if (
        // Folha vazia é a primeira da seção, menor por causa do cabeçalho. Bloco
        // que cabe numa folha de continuação e não tem corte seguro aqui vai
        // inteiro para a próxima, em vez de ser cortado no meio.
        bloco.altura <= capacidadeContinuacao + FOLGA &&
        maiorCorteAte(bloco.cortes, 0, restante) === undefined &&
        maiorCorteAte(bloco.cortesInternos ?? [], 0, restante) === undefined
      ) {
        novaFolha();
      }

      if (bloco.altura <= restante + FOLGA) {
        atual.pecas.push({ bloco: indiceDoBloco, inicio: 0, fim: bloco.altura });
        restante -= bloco.altura;
        return;
      }

      // Maior que a folha inteira: fatia nos cortes seguros.
      let inicio = 0;
      while (bloco.altura - inicio > restante + FOLGA) {
        const limite = inicio + restante;
        const corte =
          maiorCorteAte(bloco.cortes, inicio, limite) ??
          maiorCorteAte(bloco.cortesInternos ?? [], inicio, limite);
        const fim = corte ?? limite;
        if (corte === undefined) {
          avisos.push({ secao: indiceDaSecao, bloco: indiceDoBloco, motivo: 'corte-forcado' });
        }
        atual.pecas.push({ bloco: indiceDoBloco, inicio, fim });
        novaFolha();
        inicio = fim;
      }
      atual.pecas.push({ bloco: indiceDoBloco, inicio, fim: bloco.altura });
      restante -= bloco.altura - inicio;
    });
  });

  return { paginas, inicioDaSecao, avisos };
}

/** Maior corte em (inicio, limite]. */
function maiorCorteAte(cortes: number[], inicio: number, limite: number): number | undefined {
  let melhor: number | undefined;
  for (const corte of cortes) {
    if (corte > inicio + FOLGA && corte <= limite + FOLGA) melhor = corte;
  }
  return melhor;
}

/** Folhas que vêm antes da primeira seção: capa e sumário. */
export const FOLHAS_ANTES_DAS_SECOES = 2;

/** Número impresso (1 = capa) da primeira folha de cada seção, para o sumário. */
export function paginasDoSumario(paginacao: Paginacao): number[] {
  return paginacao.inicioDaSecao.map((indice) => indice + FOLHAS_ANTES_DAS_SECOES + 1);
}

/** Total de folhas do documento, contando capa e sumário. */
export function totalDeFolhas(paginacao: Paginacao): number {
  return paginacao.paginas.length + FOLHAS_ANTES_DAS_SECOES;
}
